import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { fetchUsers } from "../../../api/user";
import { debounce } from "lodash";
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  CheckIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";

const DEFAULT_EMPLOYEE_EXTRA_FILTERS = [];

/**
 * Modern Employee Selector Component
 * 
 * Features:
 * - Fast employee fetching with debounced search
 * - Multiple selection support
 * - Responsive design for mobile and desktop
 * - Compact and user-friendly
 * 
 * @param {Object} props
 * @param {boolean} props.open - Whether the selector is open
 * @param {Array} props.selectedEmployees - Array of selected employee objects
 * @param {Function} props.onChange - Callback when selection changes (receives array of employees)
 * @param {Function} props.onClose - Callback when selector should close
 * @param {boolean} props.multiple - Allow multiple selection (default: false)
 * @param {Array} props.filters - Additional filters to apply
 */
export default function ModernEmployeeSelector({
  open = false,
  selectedEmployees = [],
  onChange,
  onClose,
  multiple = false,
  filters = DEFAULT_EMPLOYEE_EXTRA_FILTERS,
  showHeader = true,
}) {
  const [searchQuery, setSearchQuery] = useState("");
  const [employees, setEmployees] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [selectedIds, setSelectedIds] = useState(
    new Set(selectedEmployees.map((e) => e.id))
  );

  const perPage = 20;

  // Update selectedIds when selectedEmployees prop changes
  useEffect(() => {
    if (open) {
      setSelectedIds(new Set(selectedEmployees.map((e) => e.id)));
    }
  }, [selectedEmployees, open]);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (open) {
      setSearchQuery("");
      setCurrentPage(1);
      setError(null);
      // Don't clear employees if we have selectedEmployees - they should be visible
      // Only clear if we don't have any selected employees
      if (selectedEmployees.length === 0) {
        setEmployees([]);
      } else {
        // Pre-populate with selected employees so they're visible immediately
        setEmployees(selectedEmployees);
      }
    } else {
      setEmployees([]);
    }
  }, [open, selectedEmployees]);

  // Create search function that will be debounced
  const searchFunction = useCallback(async (query, page, currentFilters) => {
    setIsLoading(true);
    setError(null);

    try {
      const params = {
        page: page,
        per_page: perPage,
        sortBy: "id",
        sort: "desc",
        fields: "id,name,displayTitle,email",
        filters: [
          { name: "EMPLOYEE", value: true },
          ...currentFilters,
        ],
      };

      // Add query if provided
      if (query && query.trim()) {
        params.query = query.trim();
      }

      const response = await fetchUsers(params);

      if (response && response.data) {
        if (page === 1) {
          setEmployees(response.data);
        } else {
          setEmployees((prev) => [...prev, ...response.data]);
        }

        setTotalCount(response.total_count || response.data.length);
        setHasMore(
          response.data.length === perPage &&
            (page * perPage < (response.total_count || 0))
        );
      } else {
        setEmployees([]);
        setTotalCount(0);
        setHasMore(false);
      }
    } catch (err) {
      console.error("Error fetching employees:", err);
      setError("Kunne ikke laste ansatte");
      setEmployees([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Debounced search function - use useRef to avoid recreating on every render
  const debouncedSearchRef = useRef(debounce(searchFunction, 300));

  // Update debounced function when searchFunction changes
  useEffect(() => {
    debouncedSearchRef.current.cancel();
    debouncedSearchRef.current = debounce(searchFunction, 300);
    
    return () => {
      debouncedSearchRef.current.cancel();
    };
  }, [searchFunction]);

  // Memoize filters to prevent infinite loops (only recreate if filters actually change)
  const filtersRef = useRef(filters);
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  // Fetch employees when search query changes
  useEffect(() => {
    if (!open) {
      debouncedSearchRef.current.cancel();
      setIsLoading(false);
      return;
    }
    
    // Always fetch when dialog opens (even without query)
    setCurrentPage(1);
    debouncedSearchRef.current(searchQuery, 1, filtersRef.current);

    return () => {
      debouncedSearchRef.current.cancel();
    };
  }, [searchQuery, open, filters]);

  // When dialog opens and we have selectedEmployees, ensure they're in the list
  useEffect(() => {
    if (open && selectedEmployees.length > 0 && employees.length === 0) {
      // If we have selected employees but no employees loaded yet, add them to the list
      // This ensures pre-selected users from URL params are visible
      const selectedIdsSet = new Set(selectedEmployees.map(e => e.id));
      setEmployees(selectedEmployees);
      setSelectedIds(selectedIdsSet);
    }
  }, [open, selectedEmployees, employees.length]);

  // Load more employees
  const loadMore = useCallback(() => {
    if (!isLoading && hasMore) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      debouncedSearchRef.current(searchQuery, nextPage, filtersRef.current);
    }
  }, [isLoading, hasMore, currentPage, searchQuery]);

  // Toggle employee selection
  const toggleEmployee = useCallback(
    (employee) => {
      const newSelectedIds = new Set(selectedIds);

      if (newSelectedIds.has(employee.id)) {
        newSelectedIds.delete(employee.id);
      } else {
        if (!multiple) {
          newSelectedIds.clear();
        }
        newSelectedIds.add(employee.id);
      }

      setSelectedIds(newSelectedIds);

      // Get selected employees - prioritize employees from current list
      const selectedFromCurrent = employees
        .filter((e) => newSelectedIds.has(e.id));
      const selectedFromPrevious = selectedEmployees
        .filter(
          (e) =>
            newSelectedIds.has(e.id) &&
            !employees.find((emp) => emp.id === e.id)
        );
      const selected = [...selectedFromCurrent, ...selectedFromPrevious];

      if (onChange) {
        onChange(selected);
      }

      // Auto-close for single selection
      if (!multiple && newSelectedIds.size > 0 && onClose) {
        setTimeout(() => {
          onClose();
        }, 300);
      }
    },
    [
      selectedIds,
      employees,
      selectedEmployees,
      multiple,
      onChange,
      onClose,
    ]
  );

  // Clear selection
  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
    if (onChange) {
      onChange([]);
    }
  }, [onChange]);

  // Check if employee is selected
  const isSelected = useCallback(
    (employeeId) => {
      return selectedIds.has(employeeId);
    },
    [selectedIds]
  );

  if (!open) return null;

  return (
    <div className="w-full h-full flex flex-col bg-white rounded-none sm:rounded-lg border border-gray-200 shadow-sm">
      {/* Header */}
      {showHeader && (
        <div className="flex-shrink-0 p-2 sm:p-4 border-b border-gray-200">
          <div className="flex items-center justify-between mb-2 sm:mb-3">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900">
              {multiple ? "Velg ansatte" : "Velg ansatt"}
            </h3>
            <div className="flex items-center space-x-2">
              {selectedIds.size > 0 && (
                <button
                  onClick={clearSelection}
                  className="text-sm text-gray-500 hover:text-gray-700"
                  title="Fjern alle valg"
                >
                  <XCircleIcon className="h-5 w-5" />
                </button>
              )}
              {onClose && (
                <button
                  onClick={onClose}
                  className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 transition-colors"
                  title="Lukk"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              )}
            </div>
          </div>

          {/* Search Bar */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Søk etter ansatte..."
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              autoFocus
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                <XMarkIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
              </button>
            )}
          </div>

          {/* Selected count */}
          {selectedIds.size > 0 && (
            <div className="mt-2 text-sm text-gray-600">
              {selectedIds.size} {selectedIds.size === 1 ? "ansatt" : "ansatte"} valgt
            </div>
          )}
        </div>
      )}
      
      {!showHeader && (
        <div className="flex-shrink-0 p-2 sm:p-4 border-b border-gray-200">
          {/* Search Bar */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Søk etter ansatte..."
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              autoFocus
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                <XMarkIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Employee List */}
      <div className="flex-1 overflow-y-auto max-h-[600px]">
        {isLoading && employees.length === 0 ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
              <p className="text-sm text-gray-500">Laster ansatte...</p>
            </div>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          </div>
        ) : employees.length === 0 ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <p className="text-sm text-gray-500">
                {searchQuery
                  ? "Ingen ansatte funnet"
                  : "Søk etter ansatte for å begynne"}
              </p>
            </div>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {employees.slice(0, 20).map((employee) => {
              const selected = isSelected(employee.id);
              
              return (
                <div
                  key={employee.id}
                  className={`w-full p-2 sm:p-3 border-b border-gray-100 last:border-b-0 ${
                    selected ? "bg-blue-50 border-l-4 border-l-blue-600" : ""
                  } ${!multiple ? "cursor-pointer hover:bg-gray-50" : ""}`}
                  onClick={!multiple ? () => toggleEmployee(employee) : undefined}
                >
                  <div className="flex items-start space-x-2 sm:space-x-3">
                    {/* Checkbox */}
                    <div className="flex-shrink-0 mt-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleEmployee(employee);
                        }}
                        className="focus:outline-none"
                      >
                        <div
                          className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                            selected
                              ? "bg-blue-600 border-blue-600"
                              : "border-gray-300 hover:border-blue-400"
                          }`}
                        >
                          {selected && (
                            <CheckIcon className="h-4 w-4 text-white" />
                          )}
                        </div>
                      </button>
                    </div>

                    {/* Employee Info */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {employee.displayTitle || employee.name}
                      </p>
                      {employee.email && (
                        <p className="text-xs text-gray-500 mt-0.5">
                          {employee.email}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Load More Button */}
        {hasMore && !isLoading && employees.length < 20 && (
          <div className="p-4 text-center border-t border-gray-200">
            <button
              onClick={loadMore}
              className="text-sm text-blue-600 hover:text-blue-800 font-medium"
            >
              Last flere ansatte
            </button>
          </div>
        )}

        {isLoading && employees.length > 0 && (
          <div className="p-4 text-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
          </div>
        )}
      </div>
    </div>
  );
}

